import { ProfileVO } from './profile.vo';
import { StaticFeatureEnum } from './../../manager/permission/permissions.types';
import { RoleVO } from './role.vo';
import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { UserApplication, Role, Profile } from '@wizeoo/akm-message-lib';
import { FEATURE_PERIMETER } from '../../model/enums';
import { IVo, entity, field } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';


@entity('wizeoo.allkey.user.message.UserApplication', 'BU')
export class UserApplicationVO implements IVo {
  /**
   * Code du user app pour construction de l'arbre
   */
  public code: string;

  /**
   * Label alternatif pour les BU
   */
  public altLabel: string;

  /**
   * Parent code pour construction de l'arbre
   */
  public parentCode: string;

  private _source: UserApplication;

  public get source(): UserApplication {
    return this._source;
  }

  public set source(value: UserApplication) {
    this._source = value;

    this._profiles = null;
    this._roles = null;
    this._rolesByFeature = null;
  }

  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model, source: UserApplication = new UserApplication()) {
    this._model = model;
    this._source = source;
    if (this._source.id === 0 || !this._source.id) {
      this._source.uid = WizMathUtils.createUUID();
    }
  }

  @field('string')
  public get perimeter(): string {
    return this.source.perimeter;
  }
  public set perimeter(value: string) {
    this.source.perimeter = value;
  }

  @field('number')
  public get id(): number {
    return this.source.id;
  }
  public set id(value: number) {
    this.source.id = value;
  }

  public get exist(): boolean {
    return this.id !== null && this.id !== undefined;
  }

  @field('string')
  public set uid(uid: string) {
    this.source.uid = uid;
  }
  public get uid(): string {
    return this.source.uid;
  }

  @field('string')
  public set domain(domain: string) {
    this.source.domain = domain;
  }
  public get domain(): string {
    return this.source.domain;
  }

  @field('string')
  public set businessUnitCode(businessUnitCode: string) {
    this.source.businessUnitCode = businessUnitCode;
  }
  public get businessUnitCode(): string {
    return this.source.businessUnitCode;
  }

  @field('string')
  public set visionCode(value: string) {
    this.source.visionCode = value;
  }
  public get visionCode(): string {
    return this.source.visionCode;
  }

  @field('boolean')
  public set doNone(value: boolean) {
    this.source.doNone = value;
  }
  public get doNone(): boolean {
    return this.source.doNone;
  }

  @field('boolean')
  public set main(value: boolean) {
    this.source.main = value;
  }
  public get main(): boolean {
    return this.source.main;
  }

  public set ownerId(value: number) {
    this.source.ownerId = value;
  }
  public get ownerId(): number {
    return this.source.ownerId;
  }


  /** collection de rôles */
  @field('Role', true)
  public get roles(): Role[] {
    if (!this.source.roles) {
      this.source.roles = [];
    }
    return this.source.roles;
  }
  public set roles(roles: Role[]) {
    this.source.roles = roles;
  }

  /** collection de rôles */
  @field('Profile', true)
  public get profiles(): Profile[] {
    if (!this.source.profiles) {
      this.source.profiles = [];
    }
    return this.source.profiles;
  }
  public set profiles(profiles: Profile[]) {
    this.source.profiles = profiles;
  }

  @field('string')
  public get tags(): Object {
    return this._source.tags;
  }
  public set tags(value: Object) {
    this._source.tags = value;
  }


  /* ################################################
              composites
     ################################################ */


  private _roles: RoleVO[];
  private _rolesByFeature: Map<string, RoleVO[]>;

  /**
   * Accès aux roles de l'application
   */
  public get rolesVO(): RoleVO[] {
    if (!this._roles || this._roles.length !== this.roles.length) {
      this._roles = [];

      if (!!this.roles && this.roles.length > 0) {
        this._roles = this.roles.map((r) => new RoleVO(this.model, r));
      }
    }

    return this._roles;
  }

  /**
   * Setter de la liste de roles en mode VO
   * @param value la liste de roles
   */
  public set rolesVO(value: RoleVO[]) {
    this._roles = value;
    let result: Role[] = null;
    if (value) {
      result = value.map((cn) => cn.source);
    }
    this._rolesByFeature = null;
    this.roles = result;
  }

  public get rolesByFeature(): Map<string, RoleVO[]> {
    if (!this._rolesByFeature) {
      this._rolesByFeature = this.rolesVO?.reduce((map, role) => {
        if (!map.has(role.featureCode)) {
          map.set(role.featureCode, [role]);
        } else {
          map.get(role.featureCode).push(role);
        }

        return map;
      }, new Map());
    }

    return this._rolesByFeature;
  }

  /**
   * Le user a t-il le rôle en paramètre?
   * @param role Le role testé
   * @return true si le user a le role en paramètre
   */
  public hasRole(role: RoleVO, checkProfiles: boolean = false): boolean {
    let rs = this.rolesVO;

    if (checkProfiles) {
      rs = rs.concat(this.profilesVO.map((p) => p.rolesVO).flat());
    }

    for (const r of rs) {
      if (r.code === role.code) {
        return true;
      }
    }

    return false;
  }

  /**
   * Ajout du rôle à l'app
   * @param role le rôle à ajouters
   */
  public addRole(role: RoleVO): void {
    this.roles.push(role.source);
    this.rolesVO.push(role);
    if (!this._rolesByFeature) {
      this._rolesByFeature = new Map();
    }

    if (!this._rolesByFeature.has(role.featureCode)) {
      this.rolesByFeature.set(role.featureCode, [role]);
    } else {
      this.rolesByFeature.get(role.featureCode).push(role);
    }
  }

  /**
   * Supprime le rôle pour le code donnée en paramètre
   * @param code le code du rôle à supprimer
   */
  public removeRole(code: string, featureCode: string) {
    // pas de rôles, on ne va pas plus loin
    if (!this.rolesVO || this.rolesVO.length === 0) {
      return;
    }

    let roleIndex = this.roles.findIndex((r) => r.code === code);
    if (roleIndex >= 0) {
      this.roles.splice(roleIndex, 1);
    }

    roleIndex = this.rolesVO.findIndex((r) => r.code === code);
    if (roleIndex >= 0) {
      this.rolesVO.splice(roleIndex, 1);
    }

    if (this.rolesByFeature.has(featureCode)) {
      const rols: RoleVO[] = this.rolesByFeature.get(featureCode);
      roleIndex = rols.findIndex((r) => r.code === code);
      if (roleIndex >= 0) {
        rols.splice(roleIndex, 1);
      }
    }
  }

  /**
   * Indique si le user est admin sur cette app
   *
   * @returns true si il est admin, false sinon
   */
  public isAdmin(): boolean {
    // pas de rôle admin sur ces périmètres
    if (
      this.perimeter === FEATURE_PERIMETER.APP ||
      this.perimeter === FEATURE_PERIMETER.APP_CONF ||
      this.perimeter === FEATURE_PERIMETER.WIZ
    ) {
      return false;
    }

    const roleCode: string =
      this.perimeter === FEATURE_PERIMETER.DOM ? StaticFeatureEnum.DOM_ADMIN : StaticFeatureEnum.BU_ADMIN;
    return this.rolesByFeature.has(roleCode);
  }

  /**
   * Retourne le role associé à la feature code en paramètre
   */
  public featureRole(pFeatureCode: string): RoleVO {
    if (!this.roles) {
      return null;
    }
    const role: Role = this.roles.find((r) => r.featureCode === pFeatureCode);
    return !!role ? new RoleVO(this.model, role) : null;
  }

  /**
   * Retourne tous les roles associés à une feature possédé par le user app.
   * On retrouve les rôles directs et les rôles des profiles
   *
   * @param pFeatureCode Le code de la feature
   */
  public getFeatureRoles(pFeatureCode: string): RoleVO[] {
    if (!this.roles && !this.profiles) {
      return [];
    }

    let result: RoleVO[] = [];

    // on récolte les rôles des profiles associés
    if (!!this.profilesVO && this.profilesVO.length > 0) {
      result = result.concat(
        this.profilesVO
          .map((p) => p.rolesVO)
          .flat()
          .filter((r) => r.featureCode === pFeatureCode)
      );
    }

    // on récolte les rôles associés
    if (!!this.rolesVO && this.rolesVO.length > 0) {
      result = result.concat(this.rolesByFeature.get(pFeatureCode) || []);
    }

    return result;
  }


  public get trsLabel(): string {
    return this.altLabel;
  }

  private _profiles: ProfileVO[];

  /**
   * Accès aux roles de l'application
   */
  public get profilesVO(): ProfileVO[] {
    if (!this._profiles || this._profiles.length !== this.profiles.length) {
      this._profiles = [];

      if (!!this.profiles && this.profiles.length > 0) {
        this._profiles = this.profiles.map((p) => new ProfileVO(this.model, p));
      }
    }

    return this._profiles;
  }

  /**
   * Setter de la liste de roles en mode VO
   * @param value la liste de roles
   */
  public set profilesVO(value: ProfileVO[]) {
    this._profiles = value;
    let result: Profile[] = null;
    if (value) {
      result = value.map((cn) => cn.source);
    }
    this.profiles = result;
  }

  /**
   * Indique si le profile fait partie de la liste
   * @param profile
   * @returns
   */
  public hasProfile(profile: ProfileVO): boolean {
    let result: boolean = false;
    for (const p of this.profiles) {
      if (p.code === profile.code) {
        result = true;
        break;
      }
    }
    return result;
  }

  /**
   * Ajout du profile à la liste des profiles
   *
   * @param profile le profile à ajouter
   */
  public addProfile(profile: ProfileVO): void {
    this.profiles.push(profile.source);
    // pour forcer la regénération de la liste des VO
    this._profiles = null;
  }

  public removeProfile(profile: ProfileVO): void {
    // pas de profiles donc pas de profile à remove
    if (!this.profiles || this.profiles.length === 0) {
      return;
    }

    const removeIdx: number = this.profiles.findIndex((p) => p.code === profile.code);
    if (removeIdx >= 0) {
      this.profiles.splice(removeIdx, 1);
    }

    // reset de la liste des vo pour regénération
    this._profiles = null;
  }

  /**
   * La clé d'identification unique
   */
  public get key(): string {
    return this.model.userAppEngine.key(this);
  }

  /**
   * La clé d'identification unique
   */
  public get businessKey(): string {

    switch(this.perimeter) {
      case 'DOM': 
        return this.code;
      case 'BU': 
        return this.code;
      case 'APP_CONF': 
        return this.model.userAppEngine.businessKey(this);
    }
  }

}
